import * as React from 'react';

import {
  CardsContainer,
  ICardProps,
  IInfoLink,
  IMenuLink,
} from '../components';
import { TagsFilter } from '../components/TagsFilter';

// to color the last word of a title
export const ColorTitle = (text: string): JSX.Element => {
  let newTitle;
  let coloredWord;
  if (text.split(' ').length > 1) {
    newTitle = text.substring(0, text.lastIndexOf(' '));
    coloredWord = text.substring(text.lastIndexOf(' '));
  } else {
    newTitle = text;
  }

  if (coloredWord) {
    return (
      <>
        {newTitle} <span>{coloredWord}</span>
      </>
    );
  } else return newTitle;
};

export function trackWidgetStep(widget: string, step: number): void {
  if (typeof gtag === 'function') {
    gtag('event', 'widget_step', {
      event_category: widget,
      event_label: `step ${step}`,
    });
  }
}

export function trackFormSuccess(
  event: 'New-Widget-Fenster' | 'New-Widget-Türen' | 'Absenden Katalog-Formular'
): void {
  if (typeof gtag === 'function') {
    gtag('event', event);
  }
}

export const FilteredElements = (elements: ICardProps[]): JSX.Element => {
  const tags: string[] = elements
    .flatMap((referenceInfo) => referenceInfo.tags) // Array<Array<{title: string} | null>> => Array<{title: string} | null>
    .reduce<string[]>((acc, tag) => {
      if (tag) return [...acc, tag.title];
      return acc;
    }, []); // => Array<string>
  const uniqueTags = [...new Set(tags)]; // remove duplicates
  const tagsList = uniqueTags.map((tag) => ({ label: tag, value: tag }));
  return (
    <TagsFilter tagsList={tagsList}>
      {(selectedTags) => {
        const filteredElements: ICardProps[] = selectedTags.includes('all')
          ? elements
          : elements.filter((element) => {
            const elementTags = element.tags?.map((tag) => tag.title);
            return elementTags
              ?.map((tag) => selectedTags.includes(tag))
              .some((i) => i);
          });
        return <CardsContainer elements={filteredElements} />;
      }}
    </TagsFilter>
  );
};

// Slugs hidden from the top-nav flyout (pages remain nested under category for URLs/redirects)
const NAV_HIDDEN_SLUGS: Record<string, string[]> = {
  fenster: [
    'fenster-mit-oberlicht',
    'massgeschneiderte-rundbogenfenster-aus-holz',
    'doppelfluegelfenster-viel-licht-mit-filigranen-details',
    'landhaus-fenster',
    'altbau-fenster',
    'eckfenster',
  ],
  tueren: ['nebeneingangstueren', 'haustueren-mit-sprossen'],
};

// Function that format the MenuLinks with the correct Urls depending on the language of each page
// Site-agnostic URL prefix per language. The default reproduces the German
// site's De/En scheme; the US site injects its own (via SiteConfig.routePrefix)
// so the same helpers can build '/'-rooted US URLs. Passing it keeps DE output
// byte-identical while decoupling the market-specific URL structure.
export type RoutePrefix = (language: string) => string;
const defaultRoutePrefix: RoutePrefix = (language) =>
  language === 'De' ? '/' : '/en/';

export function formatMenuLinks(
  categories: IMenuLink[],
  language: 'De' | 'En',
  routePrefix: RoutePrefix = defaultRoutePrefix
): IMenuLink[] {
  const prefix = routePrefix(language);
  return categories.map((node) => {
    const hidden = NAV_HIDDEN_SLUGS[node.slug] ?? [];
    const filteredSubPages = node.subPages?.filter(
      (subPage) => !hidden.includes(subPage.slug)
    );

    return {
      ...node,
      slug: `${prefix}${node.slug}/`,
      subPages: filteredSubPages?.map((subPage) => {
        return {
          ...subPage,
          slug: `${prefix}${node.slug}/${subPage.slug}/`,
        };
      }),
    };
  });
}

export function formatInfoLinks(
  infoLinks: IInfoLink[],
  language: 'De' | 'En',
  routePrefix: RoutePrefix = defaultRoutePrefix
): IInfoLink[] {
  const prefix = routePrefix(language);
  return infoLinks.map((node) => {
    return {
      ...node,
      url: `${prefix}${node.url}/`,
    };
  });
}

const normalizeCategorySlug = (slug?: string): string | undefined => {
  if (!slug) return undefined;
  const segments = slug.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  return segments.length ? segments[segments.length - 1] : undefined;
};

export const isReferencesCategory = (
  slug?: string,
  referenceSlugs: string[] = ['referenzen', 'references']
): boolean => {
  const normalized = normalizeCategorySlug(slug);
  return normalized !== undefined && referenceSlugs.includes(normalized);
};

export const isFuerProfisCategory = (
  slug?: string,
  fuerProfisSlugs: string[] = ['fuer-profis']
): boolean => {
  const normalized = normalizeCategorySlug(slug);
  return normalized !== undefined && fuerProfisSlugs.includes(normalized);
};